import { Link } from 'react-router-dom'
import { ServiceCard } from '@/components/ui/ServiceCard'
import { TierDivider } from '@/components/ui/TierDivider'
import { useLiveTiers } from '@/hooks/useLiveTiers'
import { useSearchStore } from '@/store/search'
import { useFilterStore } from '@/store/filters'

/** Grid column layout per tier — large modules (T1/T2) vs. compact rows (T3/T4). */
const gridFor: Record<string, string> = {
  'tier-1': 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4',
  'tier-2': 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4',
  'tier-3': 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4',
  'tier-4': 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4',
}

export function Dashboard() {
  const tiers = useLiveTiers()
  const query = useSearchStore((s) => s.query)
    .trim()
    .toLowerCase()
  const selectedTiers = useFilterStore((s) => s.tiers)

  // 1) keep only selected tiers, then 2) filter cards by name/vendor when searching.
  const byTier = tiers.filter((tier) => selectedTiers.includes(tier.id))
  const visibleTiers = query
    ? byTier
        .map((tier) => ({
          ...tier,
          services: tier.services.filter(
            (s) => s.name.toLowerCase().includes(query) || s.vendor.toLowerCase().includes(query),
          ),
        }))
        .filter((tier) => tier.services.length > 0)
    : byTier

  if (visibleTiers.length === 0) {
    return (
      <div className="grid place-items-center py-24 text-center">
        <p className="text-fg-muted font-mono text-sm">
          {query ? (
            <>
              No systems match “<span className="text-fg">{query}</span>”
            </>
          ) : (
            'No tiers selected'
          )}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-4">
      {visibleTiers.map((tier) => {
        const size = tier.id === 'tier-4' ? 'sm' : 'lg'
        return (
          <section key={tier.id} className="space-y-4">
            <TierDivider label={tier.label} />
            <div className={gridFor[tier.id]}>
              {tier.services.map((service) =>
                // Systems with a drill-down page make the whole tile the way in;
                // the rest render exactly as before.
                service.detailPath ? (
                  <Link
                    key={service.name}
                    to={service.detailPath}
                    aria-label={`${service.name} — open system detail`}
                    className="focus-visible:ring-primary/40 block h-full rounded-lg focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <ServiceCard service={service} size={size} className="h-full" />
                  </Link>
                ) : (
                  <ServiceCard key={service.name} service={service} size={size} />
                ),
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}
